import { Alert, Box, Chip, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import colors from 'common/constants/colors';

const PANEL_SX = {
  bgcolor: '#f4f6f8',
  borderRadius: 1,
  borderLeft: `4px solid ${colors.dialogCloseNavy}`,
  px: 2,
  py: 1.25,
};

const MICRO_LABEL_SX = {
  color: 'text.secondary',
  fontSize: 10,
  display: 'block',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  mb: 0.25,
};

const SKIP_REASON_LABEL = {
  ORDER_NOT_REVERSIBLE: 'Order can no longer be reversed',
  LINE_ITEM_NOT_FULFILLED: 'Not in a fulfilled state',
  ALREADY_SHIPPED: 'Already shipped',
  LOCKED_FOR_PHYSICAL_COUNT: 'Locked for physical count',
  FAILED: 'Failed — needs manual review',
};

const StatCell = ({ label, value, accent }) => (
  <Box sx={{ flex: 1 }}>
    <Typography variant='caption' sx={MICRO_LABEL_SX}>
      {label}
    </Typography>
    <Typography variant='h6' sx={{ fontWeight: 700, color: accent ?? 'text.primary' }}>
      {value}
    </Typography>
  </Box>
);
StatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  accent: PropTypes.string,
};
StatCell.defaultProps = { accent: undefined };

const RecallResultPanel = ({ outcome }) => {
  const skipped = outcome?.skipped ?? [];

  return (
    <Box sx={{ px: 2.5, py: 2 }}>
      <Box sx={{ ...PANEL_SX, display: 'flex', gap: 2, mb: 2 }}>
        <StatCell label='Returned' value={`${outcome?.quantityReturned ?? 0} units`} />
        <StatCell label='Line items requeued' value={outcome?.lineItemsAccepted ?? 0} />
        <StatCell
          label='Not returned'
          value={skipped.length}
          accent={skipped.length > 0 ? 'error.main' : undefined}
        />
      </Box>

      {outcome?.status === 'QUEUED' && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Queued — the line items are being returned in the background. The lot summary will catch
          up once it finishes.
        </Alert>
      )}

      {/* Stays on screen rather than going out as a toast: an operator who expected more units
          back has to see which orders did not move, and why. */}
      {skipped.length > 0 ? (
        <>
          <Typography variant='caption' sx={MICRO_LABEL_SX}>
            {`Not returned (${skipped.length})`}
          </Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {skipped.map((row) => (
              <Box
                key={row.lineItemId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-of-type': { borderBottom: 'none' },
                }}
              >
                <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }}>
                  {row.orderId ?? row.lineItemId}
                </Typography>
                <Chip
                  size='small'
                  color='error'
                  variant='outlined'
                  label={SKIP_REASON_LABEL[row.reason] ?? row.reason}
                  sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                />
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <Typography variant='body2' color='text.secondary'>
          Every line item holding this stock was returned.
        </Typography>
      )}
    </Box>
  );
};

RecallResultPanel.propTypes = {
  outcome: PropTypes.shape({
    status: PropTypes.string,
    quantityReturned: PropTypes.number,
    lineItemsAccepted: PropTypes.number,
    skipped: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

export default RecallResultPanel;
